import CareersBackers from 'components/modules/LandingPage/Careers/Homepage/CareersBackers'
import CareersFounders from 'components/modules/LandingPage/Careers/Homepage/CareersFounders'
import CareersHeader from 'components/modules/LandingPage/Careers/Homepage/CareersHeader'
import CareersManifesto from 'components/modules/LandingPage/Careers/Homepage/CareersManifesto'
import CareersReviews from 'components/modules/LandingPage/Careers/Homepage/CareersReviews'
import ScaledWrapper from 'components/layout/ScaledWrapper'
import Meta from 'components/modules/SEO/Meta'
import { parseLocale } from 'lib/constants/localization'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'
import { NewButtonA } from 'components/elements/NewButton'
import theme from 'stories/utils/theme'
import { URLs } from 'lib/constants/URLs'
import { useState, useRef, useEffect } from 'react'
import { Body2 } from 'stories/elements/Typography/Text'
import { workingAtKnowunityRoute } from 'lib/constants/routes'
import ArrowRight from 'assets/careers/working-at-knowunity/pointing-arrow.svg'
import CareersTeamPictures from 'components/modules/LandingPage/Careers/Homepage/CareersTeamPictures'

// Constants for scale calculation (matching ScaledWrapper)
const BASE_WIDTH = 1440
const MIN_WIDTH = 860
const MAX_WIDTH = 1440
const MIN_SCALE = 0.8
const MAX_SCALE = 1.15

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-color: #0b0b0d !important;
    width: 100%;
    margin: 0 auto;
    padding-bottom: 10px;
    .header {
        display: flex;
        flex-direction: column;
        gap: 0px;
        background-color: #0b0b0d;
        width: 100%;
        position: relative;
    }
`

const PageWrapper = styled.div`
    background-color: #0b0b0d;
    width: 100%;
    min-height: 100vh;
`

const FixedButtonContainer = styled.div<{ scale: number }>`
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    z-index: 9999;
    display: flex;
    justify-content: center;
    padding: 10px 0;
    display: flex;
    background-color: transparent;
    width: 100%;
    transform: ${({ scale }) => (scale ? `scale(${scale})` : 'scale(1)')};
    transform-origin: center top;
    transition: transform 0.5s ease-out;
    .button-container {
        max-width: 300px;
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 10px;
    }
    @media (max-width: 860px) {
        top: 52.5px;
    }
`

const AppButton = styled(NewButtonA)`
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    border-radius: 30px;
    p {
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: var(--font-inter);
        font-size: 16px;
        font-weight: 650;
        line-height: 20px;
        letter-spacing: 0.3px;
    }
`

const Careers = () => {
    const [isSticky, setIsSticky] = useState(false)
    const buttonRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [scaleFactor, setScaleFactor] = useState(1)

    useEffect(() => {
        const calculateScaleFactor = () => {
            const width = window.innerWidth

            if (width <= MIN_WIDTH) {
                return 1
            } else if (width >= BASE_WIDTH) {
                if (width >= MAX_WIDTH) {
                    return MAX_SCALE
                }
                return 1 + ((width - BASE_WIDTH) / (MAX_WIDTH - BASE_WIDTH)) * (MAX_SCALE - 1)
            } else {
                return MIN_SCALE + ((width - MIN_WIDTH) / (BASE_WIDTH - MIN_WIDTH)) * (1 - MIN_SCALE)
            }
        }

        const updateScaling = () => {
            const newScale = calculateScaleFactor()
            setScaleFactor(newScale)
        }

        updateScaling()
        window.addEventListener('resize', updateScaling)

        return () => {
            window.removeEventListener('resize', updateScaling)
        }
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return

        const handleScroll = () => {
            const currentScrollPos = window.scrollY

            if (buttonRef.current) {
                const buttonRect = buttonRef.current.getBoundingClientRect()
                const buttonPosition = buttonRect.top + window.scrollY
                const buffer = window.innerWidth < 840 ? 62.5 : 92.5
                setIsSticky(currentScrollPos > buttonPosition - buffer)
            }
        }

        handleScroll()

        window.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', handleScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
        }
    }, [])

    return (
        <PageWrapper>
            {isSticky && (
                <FixedButtonContainer scale={scaleFactor}>
                    <div className="button-container">
                        <AppButton
                            href={URLs.JobsWebsitePersonio}
                            color={theme.colors.white}
                            borderRadius={theme.borderRadius.webappButton}
                            height={44}
                            width={170}
                        >
                            <Body2
                                color={theme.colors.black}
                                fontWeight={700}
                                fontSize={18}
                                fontFamily="var(--font-inter)"
                                textAlign="center"
                            >
                                {'Open positions'}
                            </Body2>
                        </AppButton>
                        <AppButton
                            href={workingAtKnowunityRoute}
                            color={theme.colors.white}
                            borderRadius={theme.borderRadius.webappButton}
                            height={44}
                            width={170}
                        >
                            <Body2
                                color={theme.colors.black}
                                fontWeight={700}
                                fontSize={18}
                                fontFamily="var(--font-inter)"
                                textAlign="center"
                            >
                                {'Learn more'}
                                <ArrowRight />
                            </Body2>
                        </AppButton>
                    </div>
                </FixedButtonContainer>
            )}
            <ScaledWrapper>
                <Container>
                    <Meta
                        title="Careers at Knowunity"
                        description="Join our elite team building the next-generation AI study companion that is redefining what is possible in learning"
                    />
                    <div className="header">
                        <CareersHeader isSticky={isSticky} containerRef={containerRef} buttonRef={buttonRef} />
                    </div>
                    <CareersTeamPictures />
                    <CareersReviews />
                    <CareersFounders />
                    <CareersManifesto />
                    <CareersBackers />
                </Container>
            </ScaledWrapper>
        </PageWrapper>
    )
}

export default Careers

export const getServerSideProps: GetServerSideProps = async (props) => {
    const parsedLocale = parseLocale(props.locale)

    return {
        props: {
            ...(await serverSideTranslations(parsedLocale.interfaceLanguageCode, ['common', 'homepage'])),
        },
    }
}
